/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  Form,
  Icon,
  Link as PdsLink,
  Text,
  RadioButton,
} from '@puppet/react-components';
import Cd4peError from '@components/Cd4peError';
import { useListBranchesV1, useListCommitsV1 } from '@services/cd4pe/vcs';
import {
  useListEnvironmentPrefixesV2,
  useListEnvironmentsV2,
  useListPeIntegrationsV2,
} from '@services/cd4pe/peIntegrations';
import {
  useGetDeploymentPoliciesV1,
  UseGetDeploymentPoliciesV1Payload,
} from '@services/cd4pe/deployments';
import {
  DeploymentPolicyV1,
  PEIntegrationEnvironmentV2,
  PEIntegrationV2,
} from '@utils/api/cd4pe';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import { CodeProjectDetailsV1, ProjectTypeV1 } from '@puppet/cd4pe-client-ts';
import useListControlReposV1 from '@services/cd4pe/controlRepos';
import useDebounce from '@hooks/useDebounce';
import {
  DEPLOYMENT_POLICY_TYPES,
  OPTIONAL_PARAMETER_TYPES,
  PARAMETER_TYPES,
} from './utils';
import {
  getBranchField,
  getCommitField,
  getPuppetEnterpriseServerField,
  getEnvironmentField,
  getControlRepoField,
  getDeploymentPolicyField,
} from '../../formHelpers';

const defaultProps = {
  submittable: true,
  cancellable: true,
};

interface Props {
  type: 'manual' | 'stage' | 'edit';
  initialBranch?: string;
  codeProject: CodeProjectDetailsV1;
  onSubmit: (deployment: Deployment) => void;
  onCancel: () => void;
  disabled?: boolean;
  submittable?: boolean;
  cancellable?: boolean;
  submitting: boolean;
  initialState?: DeploymentFormValues;
  submitKey?: string;
  cancelKey?: string;
  projectType: ProjectTypeV1;
}

export type Deployment = {
  branch: string;
  commit: string;
  puppetEnterpriseServer: PEIntegrationV2 | undefined;
  nodeGroup: PEIntegrationEnvironmentV2 | undefined;
  deploymentPolicy: DeploymentPolicyV1 | undefined;
  timeout?: number;
  description?: string;
  prefix?: string;
  controlRepoName?: string;
  controlRepoBaseFeatureBranch?: string;
};

export type DeploymentFormValues = {
  branch: string;
  commit: string;
  puppetEnterpriseServer: string;
  nodeGroup: string;
  nodeGroupId?: string;
  deploymentPolicy: string;
  deploymentPolicyId: string;
  deploymentPolicyParameters: { [key: string]: any };
  timeout?: number;
  description?: string;
  prefix?: string;
  controlRepo: string;
  controlRepoBranch: string;
};

const getDeploymentParameterField = ({
  policy,
  name,
  type,
  value,
}: {
  policy: string;
  name: string;
  type: string;
  value?: boolean | number | string | undefined;
}) => {
  const props = {
    name,
    label: name,
    value,
    path: `deploymentPolicyParameters.${policy}.${name}`,
    'data-testid': `parameter-${name}`,
  };

  if (type && type.match(/.*Sensitive.*/)) {
    return <Form.Field {...props} type="password" placeholder={type} />;
  }

  switch (type) {
    case PARAMETER_TYPES.BOOLEAN:
    case OPTIONAL_PARAMETER_TYPES.BOOLEAN:
      return <Form.Field type="checkbox" {...props} />;
    case PARAMETER_TYPES.INTEGER:
    case PARAMETER_TYPES.FLOAT:
    case PARAMETER_TYPES.NUMERIC:
      return (
        <Form.Field
          {...props}
          placeholder={type}
          type="number"
          min={0}
          required
        />
      );
    case OPTIONAL_PARAMETER_TYPES.INTEGER:
    case OPTIONAL_PARAMETER_TYPES.FLOAT:
    case OPTIONAL_PARAMETER_TYPES.NUMERIC:
      return <Form.Field {...props} placeholder={type} type="number" min={0} />;
    default:
      return <Form.Field {...props} />;
  }
};

const DeploymentForm = ({
  type,
  initialBranch = '',
  codeProject,
  onSubmit,
  onCancel,
  disabled,
  submittable,
  cancellable,
  submitting,
  initialState,
  submitKey,
  cancelKey,
  projectType,
}: Props) => {
  const { t } = useTranslation('codeDelivery');
  const workspaceId = useWorkspaceDomain();
  const [infiniteDeploymentError, setInfiniteDeploymentError] = useState(false);
  const [values, setValues] = useState<DeploymentFormValues>(
    initialState || {
      branch: initialBranch,
      commit: '',
      puppetEnterpriseServer: '',
      nodeGroup: '',
      nodeGroupId: '',
      deploymentPolicy: '',
      deploymentPolicyId: '',
      deploymentPolicyParameters: {},
      timeout: 60,
      prefix: '',
      controlRepo: '',
      controlRepoBranch: '',
    },
  );
  const [deploymentPolicyType, setDeploymentPolicyType] = useState<
    'builtIn' | 'custom'
  >('builtIn');
  const isRegexPipeline = values.branch === 'regex';

  const branches = useListBranchesV1(
    {
      workspaceId,
      provider: codeProject.srcRepoProvider,
      name: codeProject.srcRepoName,
      project: codeProject.srcRepoId,
      organization: codeProject.srcRepoOwner,
      prefix: useDebounce(values.branch),
    },
    {
      enabled: type === 'manual',
      keepPreviousData: true,
    },
  );

  const commits = useListCommitsV1(
    {
      workspaceId,
      provider: codeProject.srcRepoProvider,
      branch: values.branch,
      name: codeProject.srcRepoName,
      project: codeProject.srcRepoId,
      organization: codeProject.srcRepoOwner,
    },
    {
      enabled: type === 'manual' && values.branch !== '',
    },
  );

  const peIntegrations = useListPeIntegrationsV2(
    {
      workspaceId,
      peIntegrationNamePrefix: useDebounce(values.puppetEnterpriseServer),
    },
    {
      keepPreviousData: true,
    },
  );

  const peIntegrationMatch = peIntegrations.data?.peIntegrations?.find(
    (peIntegration) => peIntegration.name === values.puppetEnterpriseServer,
  );

  const nodeGroups = useListEnvironmentsV2(
    {
      peIntegrationId: peIntegrationMatch?.id!,
      environmentPrefix: useDebounce(values.nodeGroup),
    },
    {
      enabled: !!peIntegrationMatch,
      keepPreviousData: true,
    },
  );

  const prefixes = useListEnvironmentPrefixesV2(
    {
      peIntegrationId: peIntegrationMatch?.id ?? '',
    },
    {
      enabled: !!peIntegrationMatch,
    },
  );

  const deploymentPoliciesPayload: UseGetDeploymentPoliciesV1Payload = {
    workspaceId,
    projectName: codeProject.name,
    projectType,
    isRegexPipeline,
  };

  if (values.controlRepo !== '') {
    deploymentPoliciesPayload.deploymentPolicyControlRepoName =
      values.controlRepo;
  }

  const deploymentPolicies = useGetDeploymentPoliciesV1(
    deploymentPoliciesPayload,
    {
      enabled: values.branch !== '',
      keepPreviousData: true,
    },
  );

  const controlRepos = useListControlReposV1(
    {
      workspaceId,
      prefix: useDebounce(values.controlRepo ?? ''),
    },
    {
      enabled: projectType === 'MODULE',
      keepPreviousData: true,
    },
  );

  const controlRepoMatch = controlRepos.data?.pages[0]?.controlRepos?.find(
    (repo) => repo.name === values.controlRepo,
  );

  const controlRepoBranches = useListBranchesV1(
    {
      workspaceId,
      provider: controlRepoMatch?.srcRepoProvider!,
      name: values.controlRepo,
      project: controlRepoMatch?.srcRepoId!,
      organization: controlRepoMatch?.srcRepoOwner!,
      prefix: useDebounce(values.controlRepoBranch ?? ''),
    },
    {
      enabled: isRegexPipeline && !!controlRepoMatch,
      keepPreviousData: true,
    },
  );

  const prefixOptions =
    prefixes.data?.prefixes.map((prefix) => ({
      value: prefix,
      label: prefix,
    })) ?? [];

  const selectedDeploymentPolicy =
    deploymentPolicies.data?.deploymentPolicies?.find(
      (policy) => policy.displayName === values.deploymentPolicy,
    ) ?? {};

  let parameterComponents: string | any[] = [];
  if (selectedDeploymentPolicy) {
    parameterComponents =
      deploymentPolicies.data?.deploymentPolicies
        ?.find((policy) => policy.displayName === values.deploymentPolicy)
        ?.parameters?.map((parameter) => {
          if (!parameter || !parameter.name || !parameter.type) {
            return null;
          }

          return getDeploymentParameterField({
            policy: selectedDeploymentPolicy.name!,
            name: parameter.name,
            type: parameter.type,
            value: parameter.value,
          });
        }) ?? [];
  }

  const onChangeHandler = (
    nameChanged: keyof DeploymentFormValues,
    newValues: DeploymentFormValues,
  ) => {
    setInfiniteDeploymentError(false);
    const newValuesCopy = { ...newValues };

    if (nameChanged === 'branch') {
      newValuesCopy.commit = '';
    }

    if (nameChanged === 'puppetEnterpriseServer') {
      newValuesCopy.nodeGroup = '';
      newValuesCopy.prefix = '';
      newValuesCopy.deploymentPolicy = '';
      newValuesCopy.controlRepo = '';
      newValuesCopy.controlRepoBranch = '';
    }

    if (nameChanged === 'deploymentPolicy') {
      const deploymentPolicy =
        deploymentPolicies.data?.deploymentPolicies?.find(
          (policy) => policy.displayName === newValuesCopy.deploymentPolicy,
        );

      if (
        deploymentPolicy &&
        !newValuesCopy.deploymentPolicyParameters[deploymentPolicy.name!]
      ) {
        const deploymentPolicyParameters =
          deploymentPolicy?.parameters?.map((parameter) => [
            parameter.name,
            parameter.value,
          ]) ?? [];

        newValuesCopy.deploymentPolicyParameters[deploymentPolicy.name!] =
          Object.fromEntries(deploymentPolicyParameters);

        setValues(newValuesCopy);
        return;
      }
    }

    if (nameChanged === 'controlRepo') {
      newValuesCopy.controlRepoBranch = '';
    }

    setValues(newValuesCopy);
  };

  const onSubmitHandler = (submitValues: DeploymentFormValues) => {
    const nodeGroup = nodeGroups?.data?.environments?.find(
      (group) => group.name === submitValues.nodeGroup,
    );

    if (nodeGroup?.environment === values.branch) {
      setInfiniteDeploymentError(true);
      return;
    }

    const puppetEnterpriseServer = peIntegrations.data?.peIntegrations?.find(
      (peIntegration) =>
        peIntegration.name === submitValues.puppetEnterpriseServer,
    );

    let deploymentPolicy = selectedDeploymentPolicy;

    const parseParameterValue = ({
      paramType,
      value,
    }: {
      paramType: string;
      value?: boolean | number | string;
    }) => {
      let parsedValue;
      switch (paramType) {
        case PARAMETER_TYPES.BOOLEAN:
        case OPTIONAL_PARAMETER_TYPES.BOOLEAN:
          return Boolean(value);
        case PARAMETER_TYPES.INTEGER:
        case OPTIONAL_PARAMETER_TYPES.INTEGER:
          return typeof value === 'string'
            ? parseInt(value as string, 10)
            : value;
        case PARAMETER_TYPES.FLOAT:
        case OPTIONAL_PARAMETER_TYPES.FLOAT:
        case PARAMETER_TYPES.NUMERIC:
        case OPTIONAL_PARAMETER_TYPES.NUMERIC:
          parsedValue = parseFloat(value as string);
          return !Number.isNaN(parsedValue) ? parsedValue : value;
        default:
          return value;
      }
    };

    const updatedParameters =
      deploymentPolicy?.parameters?.map((p) => {
        const value =
          submitValues.deploymentPolicyParameters[deploymentPolicy?.name!][
            p.name!
          ];
        return {
          ...p,
          value: parseParameterValue({
            paramType: p.type!,
            value,
          }),
        };
      }) ?? [];

    deploymentPolicy = {
      ...deploymentPolicy,
      parameters: updatedParameters,
    };

    onSubmit({
      branch: submitValues.branch ?? '',
      commit: submitValues.commit ?? '',
      puppetEnterpriseServer,
      nodeGroup,
      deploymentPolicy,
      timeout: submitValues.timeout ?? 0,
      description: submitValues.description ?? '',
      prefix: submitValues.prefix ?? '',
      controlRepoName: submitValues.controlRepo ?? '',
      controlRepoBaseFeatureBranch: submitValues.controlRepoBranch ?? '',
    });
  };

  const onChangePolicyType = (newPolicyType: 'builtIn' | 'custom') => {
    setValues({
      ...values,
      deploymentPolicy: '',
      controlRepo: '',
      controlRepoBranch: '',
    });
    setDeploymentPolicyType(newPolicyType);
  };

  const branchMatch = branches?.data?.vcsBranches?.some(
    (branch) => branch.name === values.branch,
  );

  const commitMatch = commits?.data?.vcsCommits?.some(
    (commit) => commit.sha === values.commit,
  );

  const environmentMatch = nodeGroups.data?.environments?.find(
    (nodeGroup) => nodeGroup.name === values.nodeGroup,
  );

  /**
   * This useEffect is used when the form is rendered in the edit deployment dialog. We do not have the environment
   * display name which is used in the nodeGroup field, so we need to use the nodeGroupId to find the correct environment.
   */
  useEffect(() => {
    if (
      type === 'edit' &&
      nodeGroups.data?.environments?.length &&
      values.nodeGroupId !== ''
    ) {
      const nodeGroup = nodeGroups.data?.environments?.find(
        (group) => group.id === values.nodeGroupId,
      );

      if (nodeGroup && nodeGroup?.name !== values.nodeGroup) {
        setValues((prev) => ({
          ...prev,
          nodeGroupId: '',
          nodeGroup: nodeGroup?.name ?? '',
        }));
      }
    }
  }, [type, nodeGroups, values.nodeGroup, values.nodeGroupId]);

  /**
   * This useEffect is used when the form is rendered in the edit deployment dialog. We do not have the deployment policy
   * display name which is used in the deployPolicy field, so we need to use the deploymentPolicyId to find the correct policy.
   */
  useEffect(() => {
    if (
      type === 'edit' &&
      deploymentPolicies.data?.deploymentPolicies?.length &&
      values.deploymentPolicyId !== ''
    ) {
      const deploymentPolicy =
        deploymentPolicies.data?.deploymentPolicies?.find(
          (group) => group.name === values.deploymentPolicyId,
        );

      if (
        deploymentPolicy &&
        deploymentPolicy?.displayName !== values.deploymentPolicy
      ) {
        const newValues: Partial<DeploymentFormValues> = {
          deploymentPolicyId: '',
          deploymentPolicy: deploymentPolicy?.displayName ?? '',
        };

        if (deploymentPolicy?.custom) {
          setDeploymentPolicyType('custom');

          if (deploymentPolicy?.controlRepoId) {
            const controlRepo = controlRepos.data?.pages[0]?.controlRepos?.find(
              (repo) => repo.name === deploymentPolicy.controlRepoId?.name,
            );

            if (controlRepo) {
              newValues.controlRepo = controlRepo.name;
            }
          }
        }

        setValues((prev) => ({
          ...prev,
          ...newValues,
        }));
      }
    }
  }, [
    type,
    deploymentPolicies,
    controlRepos,
    values.deploymentPolicy,
    values.deploymentPolicyId,
  ]);

  return (
    <>
      <Form
        className="deploy-form"
        data-testid="deploy-form"
        submittable={submittable}
        cancellable={cancellable}
        values={values}
        onChange={onChangeHandler}
        onSubmit={onSubmitHandler}
        onCancel={() => onCancel()}
        disabled={disabled}
        submitting={submitting}
        submitLabel={
          submitKey
            ? t(submitKey)
            : t('viewPipeline.form.deployment.buttons.submit.manual')
        }
        cancelLabel={
          cancelKey
            ? t(cancelKey)
            : t('viewPipeline.form.deployment.buttons.cancel')
        }
      >
        {type === 'manual' &&
          getBranchField({
            query: branches,
            fieldValue: values.branch,
            t,
          })}
        {type === 'manual' &&
          branchMatch &&
          getCommitField({
            query: commits,
            fieldValue: values.commit,
            branch: values.branch,
            codeProject,
            t,
          })}
        {(commitMatch || type !== 'manual') &&
          getPuppetEnterpriseServerField({
            query: peIntegrations,
            fieldValue: values.puppetEnterpriseServer,
            t,
          })}
        {!isRegexPipeline &&
          peIntegrationMatch &&
          getEnvironmentField({
            name: 'nodeGroup',
            query: nodeGroups,
            fieldValue: values.nodeGroup,
            codeProject,
            t,
          })}
        {(environmentMatch || peIntegrationMatch) && (
          <>
            <label className="rc-form-field-label rc-form-field-label-primary">
              <Trans
                t={t}
                i18nKey="viewPipeline.form.deployment.field.deploymentPolicy.label"
              >
                <PdsLink
                  size="tiny"
                  as="a"
                  data-testid="deployment-policy-help"
                  href="https://puppet.com/docs/continuous-delivery/latest/deployment_policies.html"
                  target="_blank"
                />
              </Trans>
            </label>
            <div className="deploy-form__policy-radio-buttons">
              <RadioButton
                type="radio"
                name="builtInPolicy"
                label={t(
                  'viewPipeline.form.deployment.field.deploymentPolicy.builtin.radio.button',
                )}
                value={deploymentPolicyType === 'builtIn'}
                onChange={() => onChangePolicyType('builtIn')}
              />
              <RadioButton
                type="radio"
                name="customPolicy"
                label={t(
                  'viewPipeline.form.deployment.field.deploymentPolicy.custom.radio.button',
                )}
                value={deploymentPolicyType === 'custom'}
                onChange={() => onChangePolicyType('custom')}
              />
            </div>
            {projectType === 'MODULE' &&
              deploymentPolicyType === 'custom' &&
              getControlRepoField({
                query: controlRepos,
                fieldValue: values.controlRepo,
                t,
                label: t(
                  'viewPipeline.form.deployment.field.deploymentControlRepo.label',
                ),
                'data-testid': 'select-deployment-control-repo',
              })}
            {(projectType === 'CONTROL_REPO' ||
              deploymentPolicyType === 'builtIn' ||
              (projectType === 'MODULE' &&
                deploymentPolicyType === 'custom' &&
                controlRepoMatch)) &&
              getDeploymentPolicyField({
                query: deploymentPolicies,
                fieldValue: values.deploymentPolicy,
                customPolicy: deploymentPolicyType === 'custom',
                t,
              })}
            {selectedDeploymentPolicy.name ===
              DEPLOYMENT_POLICY_TYPES.FEATURE_BRANCH_POLICY && (
              <div
                className="deploy-form__field"
                data-testid="feature-branch-policy-explanation"
              >
                <Text size="small">
                  <Icon type="rocket" className="deploy-form__rocket-icon" />
                  {t('viewPipeline.dialog.deployment.featureBranch.heading')}
                </Text>
                <Text size="small">
                  {t('viewPipeline.dialog.deployment.featureBranch')}
                </Text>
              </div>
            )}
          </>
        )}
        {projectType === 'MODULE' &&
          selectedDeploymentPolicy.name ===
            DEPLOYMENT_POLICY_TYPES.EVENTUAL_CONSISTENCY_POLICY && (
            <div className="deploy-form__field">
              <div className="deploy-form__policy-title">
                <Icon type="rocket" className="deploy-form__policy-icon" />
                {selectedDeploymentPolicy.displayName}
              </div>
              <Trans
                t={t}
                i18nKey="viewPipeline.form.deployment.policy.eventualConsistencyExplanation"
                components={{
                  1: <br />,
                  2: <div className="deploy-form__text--yellow" />,
                  3: (
                    <PdsLink
                      type={1}
                      as="a"
                      href="https://www.puppet.com/docs/continuous-delivery/5.x/deploy_module.html"
                      target="_blank"
                    />
                  ),
                }}
              />
            </div>
          )}
        {projectType === 'MODULE' &&
          isRegexPipeline &&
          selectedDeploymentPolicy.name ===
            DEPLOYMENT_POLICY_TYPES.FEATURE_BRANCH_POLICY &&
          getControlRepoField({
            query: controlRepos,
            fieldValue: values.controlRepo,
            t,
            'data-testid': 'select-feature-deployment-control-repo',
          })}
        {projectType === 'MODULE' &&
          isRegexPipeline &&
          selectedDeploymentPolicy.name ===
            DEPLOYMENT_POLICY_TYPES.FEATURE_BRANCH_POLICY &&
          controlRepoMatch &&
          getBranchField({
            query: controlRepoBranches,
            fieldValue: values.controlRepoBranch,
            name: 'controlRepoBranch',
            label: t(
              'viewPipeline.form.deployment.field.controlRepoBranch.label',
            ),
            t,
            'data-testid': 'select-control-repo-branch',
          })}
        {parameterComponents?.length && parameterComponents}
        {values.deploymentPolicy && (type === 'stage' || type === 'edit') && (
          <Form.Field
            type="number"
            name="timeout"
            label={t('viewPipeline.form.deployment.field.timeout.label')}
            placeholder={t(
              'viewPipeline.form.deployment.field.timeout.placeholder',
            )}
            min={0}
            required
          />
        )}
        {peIntegrationMatch && prefixOptions.length > 0 && (
          <Form.Field
            type="select"
            name="prefix"
            label={t('viewPipeline.form.deployment.field.prefix.label')}
            placeholder={
              prefixes.isFetching
                ? t('viewPipeline.form.deployment.field.prefix.loading')
                : t('viewPipeline.form.deployment.field.prefix.placeholder')
            }
            options={prefixOptions}
            disabled={
              prefixOptions.length === 0 ||
              prefixes.isFetching ||
              prefixes.isError
            }
          />
        )}
        {type === 'manual' && (
          <Form.Field
            type="text"
            name="description"
            label={t('viewPipeline.form.deployment.field.description.label')}
            placeholder={t(
              'viewPipeline.form.deployment.field.description.placeholder',
            )}
            required
          />
        )}
      </Form>
      <div className="deploy-form__api-errors">
        {infiniteDeploymentError && (
          <Alert type="danger">
            {t('viewPipeline.form.deployment.error.infiniteDeployment')}
          </Alert>
        )}
        <Cd4peError error={branches.error} />
        <Cd4peError error={commits.error} />
        <Cd4peError error={peIntegrations.error} />
        <Cd4peError error={nodeGroups.error} />
        <Cd4peError error={deploymentPolicies.error} />
      </div>
    </>
  );
};

export default DeploymentForm;

DeploymentForm.defaultProps = defaultProps;
